import { NzMessageService } from 'ng-zorro-antd/message';
import { NzUploadFile } from 'ng-zorro-antd/upload';
import Papa from 'papaparse';

export function getFileNameFromS3Key(s3Key: string): string {
  return s3Key.split('/').pop() ?? '';
}

export type FileAcceptedCallback = (file: NzUploadFile, rawFile: File) => void;

export function createCsvUploadBeforeUploadHandler(
  nzMessageService: NzMessageService,
  onFileAccepted: FileAcceptedCallback,
): (file: NzUploadFile) => boolean {
  return (file: NzUploadFile): boolean => {
    const isCsv = file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv');
    if (!isCsv) {
      nzMessageService.error('Only CSV files are allowed.');
      return false;
    }

    const maxFileSizeMB = 200;
    const isValidSize = (file.size || 0) / 1024 / 1024 <= maxFileSizeMB;

    if (!isValidSize) {
      nzMessageService.error(`File must be smaller than ${maxFileSizeMB} MB.`);
      return false;
    }

    const rawFile = (file as any).originFileObj || file;
    if (!(rawFile instanceof File)) {
      nzMessageService.error('Invalid file object.');
      return false;
    }

    onFileAccepted(file, rawFile);
    return false;
  };
}

export type CsvHeadersCallback = (headers: string[], recordCount: number) => void;

export function normalizeCsvHeader(header: string): string {
  return header.trim().toLowerCase();
}

export function getDuplicateCsvHeaders(headers: string[]): string[] {
  const seenHeaders = new Set<string>();
  const duplicateHeaders = new Set<string>();

  headers.forEach((header) => {
    const trimmedHeader = header.trim();
    const normalizedHeader = normalizeCsvHeader(header);

    if (!normalizedHeader) {
      return;
    }

    if (seenHeaders.has(normalizedHeader)) {
      duplicateHeaders.add(trimmedHeader);
      return;
    }

    seenHeaders.add(normalizedHeader);
  });

  return Array.from(duplicateHeaders);
}

export function getFileMetadata(file: File, nzMessageService: NzMessageService, onSuccess: CsvHeadersCallback): void {
  let headers: string[] = [];
  let count = 0;
  let isHeaderCaptured = false;

  Papa.parse(file, {
    worker: true,
    skipEmptyLines: true,

    step: (result) => {
      const row = result.data as string[];

      // First row → headers
      if (!isHeaderCaptured) {
        headers = row;
        isHeaderCaptured = true;
        return;
      }

      // Count remaining rows
      count++;
    },

    complete: () => {
      onSuccess(headers, count);
    },

    error: (error) => {
      console.error(error);
      nzMessageService.error('Failed to read CSV file.');
    },
  });
}
